import { useCallback, useState, useEffect } from "react";
import { AlertRulePresenter } from "./AlertRulePresenter";
import * as s from "./AlertRuleStyled";
import TableButton from "components/atoms/buttons/TableButton";
import { useMutation, useQueryClient } from "react-query";
import { notify } from "components/atoms/notification/Notification";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  PostAlertRequest,
  AlertRuleListResponse,
  GetAlertListResponse,
} from "api/interfaces/alertInterface.interface";
import {
  SORT_DIRECTION,
  BasePagination,
  CustomError,
  LEVEL_TYPE,
} from "api/interfaces/commonInterface.interface";
import { postAlertRuleList } from "api/alertAPI";
import { useLocation } from "react-router-dom";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { getAccountTimeFormat } from "utils/timeUtil";
import { isCheckPermission } from "utils/AccountUtil";
import useApiError from "hook/useApiError";
import { isNull } from "lodash";

import { TableColumnsType } from "antd";
import { ColumnType } from "antd/es/table";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { baseQueryInfo } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import { useAlertRules } from "hook/useAlertRules";
import * as mediaQuery from "components/MediaQuery";
import { Row } from "pages/users/UsersStyled";
import { useAuth } from "components/Auth";

const initialQuery = {
  ...baseQueryInfo,
  sortType: "createdAt",
  sortDirection: SORT_DIRECTION.DESC,
};
export function AlertRuleContainer(): JSX.Element {
  const location: any = useLocation();
  const { handleErrorNotify, lang } = useApiError();
  const { queryNavigate } = useMenu({});
  const auth = useAuth();

  const isListPage: boolean = location.pathname.endsWith("/rules");
  const isBigScreen = mediaQuery.useIsBigScreen();

  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const [isViewModal, setIsViewModal] = useState<boolean>(false);
  const isMobile = mediaQuery.useIsMobile();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const queryClient = useQueryClient();

  const [alertRules, setAlertRules] = useState<{ totalEnable: number }>({
    totalEnable: 0,
  });
  const [alertList, setAlertList] = useState<AlertRuleListResponse[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRuleId, setSelectedRuleId] = useState<AlertRuleListResponse>();
  //status
  const status = {
    totalTitle: "Total",
    totalValue: alertRules.totalEnable,
    // items: [
    //   <Text fontSize={18} color={COLORS.BUTTONS1}>
    //     Enabled {alertRules?.result[0].enabled}
    //   </Text>,
    //   <Text fontSize={18} color={COLORS.BUTTONS1}>
    //     Disabled {alertRules?.result[0].disabled}
    //   </Text>,
    // ],
    isMobileShow: {
      totalTitle: true,
      totalValue: true,
      items: false,
    },
  };
  const [alertRuleQueryInfo, setAlertRuleQueryInfo] = useState<BasePagination>({
    ...initialQuery,
  });

  const [isRowClickEditable, setIsRowClickEditable] = useState<boolean>(
    isCheckPermission("manageAlertRules", selectedAccount)
  );
  const [pending, setPending] = useState<boolean>(true);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setAlertRuleQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "createdAt"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.DESC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setAlertRuleQueryInfo((info) => {
        return {
          ...initialQuery,
        };
      });
    }
  }, [location]);

  useEffect(() => {
    if (selectedAccount) {
      setIsRowClickEditable(
        isCheckPermission("manageAlertRules", selectedAccount)
      );
    }
  }, [selectedAccount]);

  const displayCount = (
    autoApply: boolean,
    nameArray: string[],
    total: number,
    count: number
  ) => {
    if (autoApply) {
      if (total === count) {
        return "ALL";
      } else {
        if (nameArray !== undefined) {
          if (nameArray.length > 1) {
            return `${nameArray[0]}+${nameArray.length - 1}`;
          } else {
            return nameArray[0];
          }
        } else {
          return "ALL";
        }
      }
    } else {
      if (nameArray !== undefined) {
        if (nameArray.length > 1) {
          return `${nameArray[0]}+${nameArray.length - 1}`;
        } else {
          return nameArray[0];
        }
      } else {
        return "-";
      }
    }
  };
  const onHeaderCell = (column: ColumnType<AlertRuleListResponse>) => {
    return {
      onClick: () => {
        if (column.key) {
          setAlertRuleQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const isEditable = ({ isDefault, email }: AlertRuleListResponse) => {
    if (isDefault) {
      return selectedAccount.isAdminGroup || email === auth.user.email;
    }
    return selectedAccount.isAdminGroup;
  };

  // alert rules
  const columns: TableColumnsType<AlertRuleListResponse> = [
    {
      title: "Title",
      render: (render, row) => <Row isAdmin={row.isDefault}>{row.title}</Row>,
      sorter: true,
      key: "name",
      onHeaderCell: onHeaderCell,
      sortOrder: changeSortOrderAntd(alertRuleQueryInfo, "name"),
    },
    {
      title: "Recorder",
      render: (render, row) =>
        displayCount(
          row.autoApply,
          row.recorders,
          row.totalRecorderCount,
          row.count
        ),
      hidden: selectedAccount.accountLevel !== LEVEL_TYPE.EU,
      width: isMobile ? 100 : 200,
    },
    {
      title: "Organization",
      render: (render, row) =>
        row.autoApply !== undefined && row.autoApply ? `ALL` : row.count,
      align: "center",
      hidden: selectedAccount.accountLevel === LEVEL_TYPE.EU,
      width: isMobile ? 100 : 200,
    },
    {
      title: "Created By",
      sorter: true,
      key: "createdBy",
      render: (render, row) =>
        row.createdBy
          ? row.createdBy +
            (isBigScreen && row.createdBy !== "System" ? ` (${row.email})` : "")
          : "System",
      responsive: ["lg"],
      sortOrder: changeSortOrderAntd(alertRuleQueryInfo, "createdBy"),
      onHeaderCell: onHeaderCell,
      width: isBigScreen ? "20%" : 200,
    },

    {
      title: "Date Created",
      sorter: true,
      key: "createdAt",
      render: (render, row) =>
        row.createdAt !== undefined
          ? getAccountTimeFormat(row.createdAt, selectedAccount, true)
          : "",
      responsive: ["md"],
      sortOrder: changeSortOrderAntd(alertRuleQueryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "descend",
      width: 200,
    },
    {
      title: "Description",
      render: (render, row) => row.description,
      responsive: ["xl"],
      width: isBigScreen ? "15%" : 150,
    },
    {
      title: "",
      render: (render, row) => (
        <s.Row className="table-button-wrapper">
          <RBACWrapper requiredPermissions={"manageAlertRules"}>
            <TableButton
              label={isEditable(row) ? "Edit" : "View"}
              onClick={() => onClickEdit(row)}
              data-testid="alert-rule-rule-table-button-click-edit"
            />
          </RBACWrapper>
        </s.Row>
      ),

      align: "right",
      width: isMobile ? 50 : 100,
    },
  ];

  const onRowClick = (row: AlertRuleListResponse) => {
    // if (checkDisabled(row)) return;
    if (row?.alertRuleId !== undefined) {
      onClickEdit(row);
    }
    // navigate(`/alert/rules/detail`, {
    //   state: {
    //     alertRuleId: row.alertRuleId,
    //     title: row.title,
    //   },
    // });
  };

  const onModal = (type?: string) => {
    if (type === "add") {
      if (isAddModal) {
        queryClient.invalidateQueries("alertRule");
      }
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      if (isEditModal) {
        queryClient.invalidateQueries("alertRule");
      }
      setIsEditModal(!isEditModal);
    }
    if (type === "view") {
      setIsViewModal(!isViewModal);
    }
  };

  const onClickEdit = (row: AlertRuleListResponse) => {
    if (isCheckPermission("manageAlertRules", selectedAccount)) {
      setSelectedRuleId(row);
      onModal("edit");
    }
  };

  const mutationCreateAlert = useMutation(postAlertRuleList, {
    onSuccess: () => {
      setIsAddModal(false);
      queryClient.invalidateQueries("alertRule");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.alertRule.notify.create.fail);
    },
  });

  const onClickCreate = useCallback(
    (alertInfo: PostAlertRequest) => {
      //setIsAddModal(true);
      mutationCreateAlert.mutate({
        payload: alertInfo,
        accountId: selectedAccount.accountId,
      });
    },
    [mutationCreateAlert, selectedAccount.accountId]
  );
  const { alertRuleQuery } = useAlertRules({
    queryInfo: alertRuleQueryInfo,
    options: {
      enabled: isListPage,
      onSuccess: (res: GetAlertListResponse) => {
        if (res.error !== 0 && res.result === undefined) {
          setAlertRules({ totalEnable: 0 });
          setAlertList([]);
          setTotalCount(0);
          setAlertRuleQueryInfo({ ...initialQuery });
        } else {
          setAlertRules({ totalEnable: res.page.total });
          setAlertList(
            (res.result ?? []).map((alert, index) => ({ ...alert, key: index }))
          );
          setAlertRuleQueryInfo((info) => ({ ...info, ...res.page }));
        }
      },
      onError: (_err: unknown) => {
        notify("error", lang.label.alert.notify.list.error);
        setAlertRuleQueryInfo({ ...initialQuery });
      },
      onSettled: () => {
        setPending(false);
      },
    },
  });

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (alertRuleQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: alertRuleQueryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: alertRuleQueryInfo.sortType,
            sortDirection: alertRuleQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        alertRuleQuery.refetch();
      }
      setPending(true);
      // setAlertRuleQueryInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      alertRuleQueryInfo.keyword,
      alertRuleQueryInfo.pageLimit,
      alertRuleQueryInfo.sortDirection,
      alertRuleQueryInfo.sortType,
      location.pathname,
      alertRuleQuery.refetch,
      queryNavigate,
    ]
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      if (page !== alertRuleQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: alertRuleQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: alertRuleQueryInfo.keyword as string,
            sortType: alertRuleQueryInfo.sortType,
            sortDirection: alertRuleQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
        setPending(true);
        // setAlertRuleQueryInfo((info) => {
        //   return { ...info, pageNum: (page - 1) as number };
        // });
      }
    },
    [
      alertRuleQueryInfo.keyword,
      alertRuleQueryInfo.pageLimit,
      alertRuleQueryInfo.pageNum,
      alertRuleQueryInfo.sortDirection,
      alertRuleQueryInfo.sortType,
      location.pathname,
      queryNavigate,
    ]
  );

  return (
    <AlertRulePresenter
      columns={columns}
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      isViewModal={isViewModal}
      status={status}
      onClickCreate={onClickCreate}
      data={alertList}
      onRowClick={onRowClick}
      paginationInfo={alertRuleQueryInfo}
      onChangePage={onChangePage}
      paginationTotalRows={totalCount}
      onClickSearch={onClickSearch}
      selectedAccount={selectedAccount}
      selectedRule={selectedRuleId}
      isRowClickEditable={isRowClickEditable}
      tablePending={pending}
    />
  );
}
